import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../../lib/config';

const API = API_BASE;

const EmployerCreditCardRegistration = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [existingCard, setExistingCard] = useState(null);
  const [cardData, setCardData] = useState({
    card_number: '',
    expiry_month: '',
    expiry_year: '',
    cvv: '',
    cardholder_name: '',
    billing_address: {
      postal_code: '',
      prefecture: '',
      city: '',
      address_line: ''
    }
  });
  const [cardType, setCardType] = useState('');

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      toast.error('事業者権限が必要です');
      navigate('/');
      return;
    }

    fetchExistingCard();
  }, [user, navigate]);

  const fetchExistingCard = async () => {
    try {
      const response = await fetch(`${API}/employer/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setExistingCard(data[0]); // 最初のカードを表示
        }
      }
    } catch (err) {
      console.error('Error fetching existing card:', err);
    }
  };

  const detectCardType = (number) => {
    const cleanNumber = number.replace(/\s/g, '');

    if (/^4/.test(cleanNumber)) {
      return 'visa';
    } else if (/^5[1-5]/.test(cleanNumber) || /^2[2-7]/.test(cleanNumber)) {
      return 'mastercard';
    } else if (/^3[47]/.test(cleanNumber)) {
      return 'amex';
    } else if (/^35/.test(cleanNumber)) {
      return 'jcb';
    }
    return '';
  };

  const formatCardNumber = (value) => {
    const cleanValue = value.replace(/\s/g, '');
    const cardType = detectCardType(cleanValue);
    setCardType(cardType);

    // 4桁ごとにスペースを入れる
    const formatted = cleanValue.replace(/(.{4})/g, '$1 ').trim();
    return formatted.substring(0, 19); // 16桁 + 3スペース
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setCardData(prev => ({ ...prev, card_number: formatted }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // バリデーション
      const cleanCardNumber = cardData.card_number.replace(/\s/g, '');
      if (cleanCardNumber.length < 15 || cleanCardNumber.length > 16) {
        toast.error('有効なカード番号を入力してください');
        return;
      }

      if (!cardData.expiry_month || !cardData.expiry_year) {
        toast.error('有効期限を入力してください');
        return;
      }

      if (cardData.cvv.length < 3) {
        toast.error('有効なCVVを入力してください');
        return;
      }

      // カード情報をStripe形式で送信
      const cardPayload = {
        card_number: cleanCardNumber,
        exp_month: parseInt(cardData.expiry_month),
        exp_year: parseInt(cardData.expiry_year),
        cvc: cardData.cvv,
        name: cardData.cardholder_name,
        address_zip: cardData.billing_address.postal_code,
        address_state: cardData.billing_address.prefecture,
        address_city: cardData.billing_address.city,
        address_line1: cardData.billing_address.address_line,
        address_country: 'JP'
      };

      const response = await fetch(`${API}/employer/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(cardPayload)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('クレジットカードを登録しました');
        setExistingCard(result);

        // フォームをリセット
        setCardData({
          card_number: '',
          expiry_month: '',
          expiry_year: '',
          cvv: '',
          cardholder_name: '',
          billing_address: {
            postal_code: '',
            prefecture: '',
            city: '',
            address_line: ''
          }
        });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'クレジットカードの登録に失敗しました');
      }
    } catch (err) {
      console.error('Error registering card:', err);
      toast.error(err.message || 'クレジットカードの登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async () => {
    if (!window.confirm('登録されているクレジットカードを削除しますか？')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/employer/payment-methods/${existingCard.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('クレジットカードを削除しました');
        setExistingCard(null);
      } else {
        throw new Error('クレジットカードの削除に失敗しました');
      }
    } catch (err) {
      console.error('Error deleting card:', err);
      toast.error('クレジットカードの削除に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getCardIcon = (type) => {
    const icons = {
      visa: '',
      mastercard: '',
      amex: '',
      jcb: ''
    };
    return icons[type] || '';
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 15; i++) {
      years.push(currentYear + i);
    }
    return years;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* ヘッダー */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/employer/account-settings')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            アカウント設定に戻る
          </button>
          <h1 className="text-3xl font-bold text-gray-900">クレジットカード登録</h1>
          <p className="text-gray-600 mt-2">求職者への報酬支払いに使用するクレジットカードを登録します</p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* セキュリティ情報 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <ShieldCheckIcon className="w-6 h-6 text-blue-600 mr-3 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">セキュリティについて</h3>
                <p className="text-sm text-blue-700 mt-1">
                  カード情報はSSL暗号化通信により保護され、Stripe社の安全なサーバーで管理されます。
                  カード番号はトークン化されて保存され、当サービスでは保持いたしません。
                </p>
              </div>
            </div>
          </div>

          {/* 既存カード表示 */}
          {existingCard && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">登録済みクレジットカード</h3>
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3"></span>
                    <div>
                      <p className="font-medium text-gray-900">
                        **** **** **** {existingCard.last4}
                      </p>
                      <p className="text-sm text-gray-600">
                        {existingCard.brand?.toUpperCase()}  {existingCard.exp_month}/{existingCard.exp_year}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleDeleteCard}
                    disabled={loading}
                    className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                  >
                    削除
                  </button>
                </div>
              </div>

              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700">
                  このカードは求職者への報酬支払いに使用されます。
                </p>
              </div>
            </div>
          )}

          {/* カード登録フォーム */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              {existingCard ? '新しいクレジットカードを登録' : 'クレジットカード情報'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* カード番号 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  カード番号 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={cardData.card_number}
                    onChange={handleCardNumberChange}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-12"
                    placeholder="1234 5678 9012 3456"
                    required
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    {cardType ? (
                      <span className="text-lg">{getCardIcon(cardType)}</span>
                    ) : (
                      <CreditCardIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* 有効期限とCVV */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    月 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={cardData.expiry_month}
                    onChange={(e) => setCardData(prev => ({ ...prev, expiry_month: e.target.value }))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">月</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month.toString().padStart(2, '0')}>
                        {month.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    年 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={cardData.expiry_year}
                    onChange={(e) => setCardData(prev => ({ ...prev, expiry_year: e.target.value }))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">年</option>
                    {generateYearOptions().map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CVV <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={cardData.cvv}
                    onChange={(e) => setCardData(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '').substring(0, 4) }))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="123"
                    maxLength="4"
                    required
                  />
                </div>
              </div>

              {/* カード名義人 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  カード名義人 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={cardData.cardholder_name}
                  onChange={(e) => setCardData(prev => ({ ...prev, cardholder_name: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="CARD HOLDER"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">カードに記載されている名前をローマ字で入力してください</p>
              </div>

              {/* 請求先住所 */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">請求先住所</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">郵便番号</label>
                    <input
                      type="text"
                      value={cardData.billing_address.postal_code}
                      onChange={(e) => setCardData(prev => ({
                        ...prev,
                        billing_address: { ...prev.billing_address, postal_code: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">都道府県</label>
                    <input
                      type="text"
                      value={cardData.billing_address.prefecture}
                      onChange={(e) => setCardData(prev => ({
                        ...prev,
                        billing_address: { ...prev.billing_address, prefecture: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="東京都"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">市区町村</label>
                  <input
                    type="text"
                    value={cardData.billing_address.city}
                    onChange={(e) => setCardData(prev => ({
                      ...prev,
                      billing_address: { ...prev.billing_address, city: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="渋谷区"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">住所</label>
                  <input
                    type="text"
                    value={cardData.billing_address.address_line}
                    onChange={(e) => setCardData(prev => ({
                      ...prev,
                      billing_address: { ...prev.billing_address, address_line: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="渋谷1-1-1"
                  />
                </div>
              </div>

              {/* 注意事項 */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                  <div className="text-sm text-yellow-700">
                    <p className="font-medium mb-1">ご注意</p>
                    <ul className="space-y-1">
                      <li> 登録されたカードは求職者への報酬支払いにのみ使用されます</li>
                      <li> 支払い時には事前に確認メールを送信いたします</li>
                      <li> カード情報は暗号化されて安全に保管されます</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 送信ボタン */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      登録中...
                    </div>
                  ) : (
                    'カードを登録'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerCreditCardRegistration;